import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppStore } from '../store';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  userRole: string | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, role: string) => Promise<{ error: Error | null, data: any | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authTimeout, setAuthTimeout] = useState<NodeJS.Timeout | null>(null);
  const [authRetries, setAuthRetries] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const setCurrentUser = useAppStore(state => state.setCurrentUser);

  // Function to clear auth timeout
  const clearAuthTimeout = () => {
    if (authTimeout) {
      clearTimeout(authTimeout);
      setAuthTimeout(null);
    }
  };

  // Function to set auth timeout - increased to 30 seconds
  const setAuthTimeoutHandler = () => {
    clearAuthTimeout();
    const timeout = setTimeout(() => {
      if (isLoading) {
        console.warn('Auth timeout: Session verification is taking longer than expected');
        
        // If we've retried less than 3 times, try again
        if (authRetries < 3) {
          console.log(`Retrying session verification (attempt ${authRetries + 1}/3)...`);
          setAuthRetries(prev => prev + 1);
          initializeAuth(); // Retry the auth initialization
        } else {
          console.error('Auth timeout: Session verification failed after multiple attempts');
          setIsLoading(false);
          setSession(null);
          setUser(null);
          setUserRole(null);
          // Don't navigate here - let the protected route handle it
        }
      }
    }, 30000); // Increased to 30 seconds
    setAuthTimeout(timeout);
  };

  // Initialize auth function - extracted to be reusable for retries
  const initializeAuth = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      setAuthTimeoutHandler();
      
      // Get session from Supabase
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        // Check if the error is related to an invalid refresh token
        if (error.message?.includes('Invalid Refresh Token') || 
            error.message?.includes('Refresh Token Not Found')) {
          console.warn('Invalid refresh token detected, signing out user');
          await signOut(); // This will clear the session and redirect to login
          return;
        }
        throw error;
      }
      
      if (session) {
        setSession(session);
        setUser(session.user);
        
        // Get user role from app_role field in auth.users
        // Check multiple possible locations for the role
        const role = session.user.app_role || 
                    session.user.app_metadata?.app_role || 
                    session.user.user_metadata?.app_role;
        
        console.log('User role detected:', role);
        
        setUserRole(role);
        setCurrentUser({
          id: session.user.id,
          email: session.user.email,
          role: role,
        });

        // Only redirect if we're on the login page
        if (location.pathname === '/login') {
          const lastPath = localStorage.getItem('lastPath') || '/';
          navigate(lastPath);
        }
      } else {
        // No session found
        setSession(null);
        setUser(null);
        setUserRole(null);
        
        // If we're not on an auth page, save the current path
        if (location.pathname !== '/login' && location.pathname !== '/reset-password') {
          localStorage.setItem('lastPath', location.pathname);
        }
      }
    } catch (error: any) {
      console.error('Error initializing auth:', error);
      
      // Check for refresh token errors in the catch block as well
      if (error.message?.includes('Invalid Refresh Token') || 
          error.message?.includes('Refresh Token Not Found')) {
        console.warn('Invalid refresh token detected in error, signing out user');
        await signOut();
        return;
      }
      
      // Clear state on other errors
      setSession(null);
      setUser(null);
      setUserRole(null);
    } finally {
      setIsLoading(false);
      clearAuthTimeout();
      setAuthRetries(0); // Reset retry counter on success or final failure
    }
  };

  // Save current path to localStorage when it changes
  // But only if we're not on an auth page and the user is logged in
  useEffect(() => {
    if (user && location.pathname !== '/login' && location.pathname !== '/reset-password') {
      localStorage.setItem('lastPath', location.pathname);
    }
  }, [location.pathname, user]);

  // Initialize auth on component mount
  useEffect(() => {
    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change event:', event);
        
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.warn('Token refresh failed, signing out user');
          await signOut();
          return;
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Get user role from app_role field in auth.users
          const role = session.user.app_role || 
                      session.user.app_metadata?.app_role || 
                      session.user.user_metadata?.app_role;
          
          console.log('Auth state change - role:', role);
          setUserRole(role);
          setCurrentUser({
            id: session.user.id,
            email: session.user.email,
            role: role,
          });
          
          // If this is a sign-in event, navigate to last path or home
          if (event === 'SIGNED_IN') {
            const lastPath = localStorage.getItem('lastPath') || '/';
            navigate(lastPath);
          }
        } else {
          setUserRole(null);
          setCurrentUser(null);
          
          // If this is a sign-out event, clear the last path
          if (event === 'SIGNED_OUT') {
            localStorage.removeItem('lastPath');
          }
        }
        
        // Always ensure loading is set to false after auth state change
        setIsLoading(false);
        clearAuthTimeout();
      }
    );

    return () => {
      subscription.unsubscribe();
      clearAuthTimeout();
    };
  }, [navigate, location.pathname]);

  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (!error && data.session) {
        // Session is available immediately after sign in
        setSession(data.session);
        setUser(data.user);
        
        // Get user role
        const role = data.user?.app_role || 
                    data.user?.app_metadata?.app_role || 
                    data.user?.user_metadata?.app_role;
        
        setUserRole(role);
        setCurrentUser({
          id: data.user.id,
          email: data.user.email,
          role: role,
        });
        
        // Navigate to last path or home page
        const lastPath = localStorage.getItem('lastPath') || '/';
        navigate(lastPath);
      }
      
      return { error };
    } catch (error) {
      return { error: error as Error };
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string, role: string) => {
    try {
      setIsLoading(true);
      
      // Create user with role directly in the auth.users table
      const { data: authData, error: authError } = await supabase.functions.invoke('create-user', {
        body: { email, password, role }
      });

      if (authError) {
        throw new Error(authError.message);
      }

      return { error: null, data: authData };
    } catch (error) {
      console.error('Error signing up:', error);
      return { error: error as Error, data: null };
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      // Attempt to sign out from Supabase, but don't let failures block the local cleanup
      await supabase.auth.signOut().catch(error => {
        console.warn('Supabase signOut failed:', error);
        // Continue with local cleanup even if server-side logout fails
      });
    } catch (error) {
      console.error('Error during sign out:', error);
    } finally {
      // Always clear local state and redirect, regardless of server-side logout success
      setSession(null);
      setUser(null);
      setUserRole(null);
      setCurrentUser(null);
      localStorage.removeItem('lastPath');
      setIsLoading(false);
      navigate('/login');
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      return { error };
    } catch (error) {
      return { error: error as Error };
    } finally {
      setIsLoading(false);
    }
  };

  const updatePassword = async (password: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.updateUser({
        password,
      });
      return { error };
    } catch (error) {
      return { error: error as Error };
    } finally {
      setIsLoading(false);
    }
  };

  // Check if user is admin - this is a critical check that determines menu access
  const isAdmin = userRole === 'Administrador';

  const value = {
    session,
    user,
    userRole,
    isLoading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};